// ============================================================
// TasksView.tsx
// Phase 2f sidebar Tasks zone — main-area takeover.
// List view + create/edit form, role-gated by authUser.role.
// Tasks are server-stored (see backend taskController.ts);
// create/update/delete go through store actions which keep the
// cached tasks[] array in sync optimistically.
//
// Two internal modes:
//   - 'list': filterable task list, "+ New task" CTA, click-to-edit
//   - 'form': create OR edit a single task; save-on-submit
//
// Filter strategy: server-side. Each filter change re-fetches via
// loadTasks({ status?, assignedToUserId? }). "My tasks only" passes
// the current user's id; "Show all firm tasks" passes nothing.
//
// Assignee gate: admins get a real <select> populated from
// listUsersApi (firm users, loaded once on mount into local
// firmUsers state). Workers see a read-only "Assigned to you"
// pill — backend enforces non-admins-only-assign-self, so the
// frontend gating is purely UX.
// ============================================================
import { BTN_PRIMARY_MD, BTN_SECONDARY_SM } from '../styles/buttons'
import LastEditedLine from '../components/shared/LastEditedLine'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useCasevoStore } from '../store/useCasevoStore'
import { goBack, navigateTo } from '../navigation/navigate'
import { useDraftDirty, useUnsavedChangesGuard } from '../hooks/useUnsavedChangesGuard'
import { usePresenceRoom } from '../hooks/usePresence'
import { presence } from '../services/presence'
import { useFirmChange } from '../utils/liveEvents'
import { subscribeLocalEntityChanges } from '../services/localEntityChanges'
import { scheduleTaskSave, flushTaskAutoSave, cancelTaskSave, applyRemoteTaskEdit } from '../hooks/useTaskAutoSave'
import { setTaskBaseUpdatedAt, getTaskBaseUpdatedAt, setTaskBaseValues } from '../hooks/saveBaseVersion'
import { Task, TaskStatus, CreateTaskPayload, TaskPriority, UpdateTaskPayload } from '../types/tasks.types'
import { RawCase } from '../types/case.types'
import { fetchCases, getTaskByIdApi, getTaskSummaryApi, fetchCalendarApi, listTasksApi } from '../api/casevoApi'
import AppHeader from '../components/layout/AppHeader'
import BackButton from '../components/layout/BackButton'
import { SidebarHamburger } from '../components/layout/Sidebar'
import UserAvatar from '../components/shared/UserAvatar'
import RichTextEditor, { flushCollabSnapshots } from '../components/shared/RichTextEditor'
import { useCollabUser } from '../hooks/useCollabUser'
import DOMPurify from 'dompurify'
import DateInput from '../components/shared/DateInput'
import { combineDue } from '../utils/taskDue'
import { taskDueWarning } from '../utils/caseDateRules'
import { londonToday } from '../utils/civilDate'
import DateTimeRow from '../components/shared/DateTimeRow'
import FormField from '../components/shared/FormField'
import FieldError from '../components/shared/FieldError'
import { ErrorNote } from '../components/shared/ActionBox'
import ConfirmDialogue from '../components/shared/ConfirmDialogue'
import ClientPickerModal from '../components/shared/ClientPickerModal'
import { normalisePriority, PRIORITY_LABELS, PriorityGlyph } from '../components/shared/TaskRow'
import MyWork, { MyWorkScope } from '../components/tasks/MyWork'
import { quickUpdateTask, taskPermsFor } from '../components/tasks/taskActions'
import { startOfLocalDay, startOfLocalWeek, ymdLocal } from '../components/tasks/myWorkLogic'
import type { TaskSummary } from '../types/tasks.types'
import { scopeTaskCache } from '../utils/taskScopeCache'
import type { CalendarEntry } from '../types/calendar.types'
import CaseLink from '../components/shared/CaseLink'
import { caseName, caseClientName, caseNameWithRef, caseRef, caseShortId } from '../utils/caseName'
import { AssigneeChips } from '../components/shared/AudienceControl'
import TaskProvenance from '../components/tasks/TaskProvenance'
import TaskAssigneesField, { assignStateOf, assignPayload, type TaskAssignState } from '../components/tasks/TaskAssigneesField'
import FavouriteStar from '../components/shared/FavouriteStar'
import { useCalendarDarkPref } from '../utils/calendarDarkPref'

// ── ICONS (module-scope hand-rolled) ─────────────────────────
// Clipboard with check — empty-state glyph, casevo-mint fill.
const TasksEmptyIcon: React.FC = () => (
  <svg width="72" height="72" viewBox="0 0 72 72" fill="none" aria-hidden="true" className="mb-6 text-casevo-brand opacity-60">
    <rect x="20" y="14" width="32" height="44" rx="3" stroke="currentColor" strokeWidth="2.5" fill="white" />
    <rect x="28" y="10" width="16" height="8" rx="2" stroke="currentColor" strokeWidth="2.5" fill="white" />
    <polyline points="28 36 34 42 46 30" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)
// Plus glyph for "+ New task" button.
const PlusIcon: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
)

// ── Hero-band line icons (Feather-style, currentColor) — no emojis, no lib.
// Mirrors the AppointmentFormModal icon set so the two "modals" share a family.
const IcBase: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    className={className ?? 'w-4 h-4 inline-block align-[-3px] shrink-0'} aria-hidden="true">{children}</svg>
)
const IcTask: React.FC<{ className?: string }> = (p) => <IcBase {...p}><path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></IcBase>
const IcCalendar: React.FC<{ className?: string }> = (p) => <IcBase {...p}><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></IcBase>
const IcCheckCircle: React.FC<{ className?: string }> = (p) => <IcBase {...p}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><path d="M22 4L12 14.01l-3-3" /></IcBase>
const IcFile: React.FC<{ className?: string }> = (p) => <IcBase {...p}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /></IcBase>
const IcNote: React.FC<{ className?: string }> = (p) => <IcBase {...p}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></IcBase>
const IcClientLink: React.FC<{ className?: string }> = (p) => <IcBase {...p}><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></IcBase>

// Section-heading class helper (mirrors the appointment modal's bhCls).
const bhCls = 'flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide text-casevo-mid mb-3'
// The view-mode notes panel — shared by the stored-copy render and the live
// read-only fallback (+ its loading skeleton) so swapping source never restyles.
const NOTE_PANEL_CLS = 'bg-casevo-input dark:bg-[#1f2e26] border-l-[3px] border-casevo-light rounded-r-lg px-4 py-3 text-sm leading-relaxed text-casevo-forest [&_p]:m-0 [&_p]:mb-2 [&_p:last-child]:mb-0'

// ── Due-date display helpers for the VIEW hero band ──────────────
// All-day tasks are stored at midnight UTC; format them from the plain
// 'YYYY-MM-DD' date parts so a negative-offset timezone can't shift the
// shown day. Timed tasks render their LOCAL instant (matching the grid).
const fmtYmd = (ymd: string): string => {
  const [y, m, d] = ymd.split('-').map(Number)
  if ([y, m, d].some((n) => Number.isNaN(n))) return ymd
  return new Date(y, m - 1, d).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })
}
const fmtInstant = (iso: string): string =>
  `${new Date(iso).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })} · ${new Date(iso).toLocaleTimeString('en-GB', { hour: 'numeric', minute: '2-digit', hour12: true })}`
const fmtDay = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })

// Stage 1e live edit-sync: task fieldKey (label-slug) → store/form field.
// Tasks sync on-blur per field, room-scoped to task:<id>; handled here (not
// the central engine) because the value lives in the form's LOCAL state and
// the open task id is editingTask.
// NOTE (Stage 4): `task.description` is now a Yjs collab rich-text field (doc
// `task:<id>:description`), so it's intentionally NOT in this near-live map —
// its content syncs via Hocuspocus (co-editing), not the on-blur/typing relay,
// so it never double-syncs. Title/due-date/status/assignee stay near-live.
const TASK_KEY_TO_FIELD: Record<string, keyof Task> = {
  'task.title': 'title',
  'task.due-date': 'dueDate',
  'task.status': 'status',
  'task.priority': 'priority',
  'task.assigned-to': 'assignedToUserId',
}

// The task fields that are FREE-TEXT typing (so they broadcast near-live on a
// typing debounce). Title (text input) + description (textarea) only — due-date
// (date picker), status + assignee (selects) have no meaningful intermediate
// typed state and already sync on change/blur. Mirrors usePresence's text-only
// rule for the central engine, scoped explicitly here since tasks live outside
// flushReadBroadcast.
const TASK_TEXT_KEYS = new Set<string>(['task.title'])
const TASK_INPUT_DEBOUNCE_MS = 400

// ★ MY WORK per-scope cache (module-level, so it also survives leaving and
// re-entering My work). Keyed `${userId}:${fetchKey}` where fetchKey is the
// SERVER query the scope needs: 'mine' | 'firm' (Team shares Firm's list —
// Team is a client-side slice of it) | 'unclaimed'. A hit renders
// instantly and is then silently re-validated in the background; only a
// scope's FIRST load shows the dim + spinner treatment. The entry for the
// scope on screen is kept in step with every live patch / quick action (the
// mirror effect below), so switching back never shows a pre-edit list.
type TaskFetchKey = 'mine' | 'firm' | 'unclaimed' | 'setByMe'
// Does a row belong to the list on screen? The SAME rule as each server query:
//   mine      — lead / co-assignee / firm-wide (never "merely created")
//   unclaimed — firm-wide with nobody beyond the lead
//   setByMe   — I created it
//   firm/team — anything the server delivered to me (access-filtered)
const taskBelongsToScope = (t: Task, k: TaskFetchKey, me: string): boolean => {
  const onIt = t.assignedToUserId === me || (t.assignees ?? []).some((a) => a.userId === me)
  if (k === 'mine') return onIt || t.firmWide === true
  if (k === 'unclaimed') return t.firmWide === true && (t.assignees ?? []).every((a) => a.userId === t.assignedToUserId)
  if (k === 'setByMe') return t.createdByUserId === me
  return true
}
// (module in utils so logout can clear it — see taskScopeCache.ts)
// My work team-load panel: silent re-poll cadence (see the poll effect).
const TEAM_LOAD_POLL_MS = 30000

// ── MAIN VIEW ────────────────────────────────────────────────
const TasksView: React.FC = () => {
  const authUser = useCasevoStore((s) => s.authUser)
  const currentUserId = authUser?.id ?? ''
  const isAdmin = authUser?.role === 'admin'
  const tasks = useCasevoStore((s) => s.tasks)
  const tasksLoading = useCasevoStore((s) => s.tasksLoading)
  const tasksError = useCasevoStore((s) => s.tasksError)
  const createTask = useCasevoStore((s) => s.createTask)
  const deleteTask = useCasevoStore((s) => s.deleteTask)
  const clients = useCasevoStore((s) => s.clients)
  const loadClients = useCasevoStore((s) => s.loadClients)
  const pendingTaskId = useCasevoStore((s) => s.pendingTaskId)
  const setPendingTaskId = useCasevoStore((s) => s.setPendingTaskId)
  const pendingNewTaskClientId = useCasevoStore((s) => s.pendingNewTaskClientId)
  const setPendingNewTaskClientId = useCasevoStore((s) => s.setPendingNewTaskClientId)
  const pendingNewTaskDueDate = useCasevoStore((s) => s.pendingNewTaskDueDate)
  const setPendingNewTaskDueDate = useCasevoStore((s) => s.setPendingNewTaskDueDate)
  const pendingNewTaskDueTime = useCasevoStore((s) => s.pendingNewTaskDueTime)
  const setPendingNewTaskDueTime = useCasevoStore((s) => s.setPendingNewTaskDueTime)
  // NB: taskOpenedFrom is read + cleared LIVE via useCasevoStore.getState()
  // inside closeFormToOrigin (so a stale render-closure can never mask the
  // origin at exit time) — hence no hook-level selector/setter here.
  // Deep-link deadline/task feature: taskIntentId is DISTINCT from
  // pendingTaskId. pendingTaskId opens the edit form (existing
  // behaviour); taskIntentId only scrolls + highlights the row in the
  // list. Both intents are cleared on first read.
  const taskIntentId = useCasevoStore((s) => s.taskIntentId)
  // One-shot preset from the dashboard OVERDUE card. When set, this
  // view opens with the OVERDUE filter already ON (consumed + cleared
  // by the mount effect below).
  const pendingTasksOverdue = useCasevoStore((s) => s.pendingTasksOverdue)
  const setPendingTasksOverdue = useCasevoStore((s) => s.setPendingTasksOverdue)

  // Collab identity for the Yjs carets on the task description editor (Stage 4).
  const collabUser = useCollabUser()
  // When a task is opened FROM the calendar (taskOpenedFrom==='calendar'), the
  // form renders as a CENTERED OVERLAY carrying the calendar's OWN focus theme
  // (dark/light), mirroring how AppointmentFormModal opens over the calendar —
  // so the task doesn't jar with the app theme or dump at the top of the page.
  // A list-opened task keeps the normal in-page card + app theme.
  const taskOpenedFrom = useCasevoStore((s) => s.taskOpenedFrom)
  const calendarDark = useCalendarDarkPref((s) => s.dark)
  const openedFromCalendar = taskOpenedFrom === 'calendar'
  // Mode + filter state
  const [mode, setMode] = useState<'list' | 'form'>('list')
  const [editingTask, setEditingTask] = useState<Task | null>(null)
  // Within form mode, an existing task opens read-only ('view') first; a new
  // task opens straight into the 'edit' form. Seeded in the form-seed effect,
  // flipped by the VIEW "Edit" button + EDIT "Cancel" (back to view).
  const [taskView, setTaskView] = useState<'view' | 'edit'>('edit')
  // Presence ROOM for the task edit surface: `task:<id>` while editing an
  // existing task, null for the list or a brand-new (unsaved) task. Field
  // presence (task.* keys) scopes to it.
  usePresenceRoom(editingTask ? `task:${editingTask.id}` : null)
  // Deep-link feature: transient highlight for a deep-linked task
  // row. Set by the taskIntentId mount effect below; cleared by a
  // 2000ms setTimeout so the ring fades cleanly.
  const [highlightedTaskId, setHighlightedTaskId] = useState<string | null>(null)
  // Worker rule: a task is the worker's OWN when they are its OWNER
  // (assignedToUserId) or its creator — they get full edit. Only a task someone
  // ELSE owns (e.g. an admin's task they're co-assigned to) is status-only.
  // Admins can fully edit anything. (Owner-aware: keying on the creator alone
  // mis-labelled a task the worker owns as "assigned by someone else".)
  // Creator counts only while still ON the task (owner or co-assignee): off
  // the task the creator keeps VIEW access (and may change who it's with from
  // the list's reassign popover) but not edit its details (backend: 403).
  // (Rules shared with the client Tasks tab via taskPermsFor — taskActions.ts.)
  const perms = taskPermsFor(authUser)
  const { isOnTaskAsPerson } = perms
  // ★ "Edit colleagues' tasks" (editColleaguesTasks): full edit of ANY task the
  // user can see — the backend grants it only together with seeFirmWideTasks
  // (lib/taskAccess checkTaskAccess / taskController.updateTask), so the UI
  // requires both too (never more permissive than the server). Delete is NOT
  // included (canDeleteTask below stays owner/admin, as deleteTask).
  const { canEditColleaguesTasks } = perms
  const canEditTaskFully = perms.canEditFully
  const canEditFully = !editingTask || canEditTaskFully(editingTask)
  // Status may be changed by the task's audience (owner / co-assignee / firm-wide)
  // or an admin — updateTask's rule (a "See colleagues' tasks" viewer outside the
  // audience is view-only) — or by a colleague-editor (editColleaguesTasks).
  // Same rule as My Work's row canStatus.
  const canChangeStatus = !editingTask || isAdmin || !!editingTask.firmWide || isOnTaskAsPerson(editingTask) || canEditColleaguesTasks
  // Delete is OWNER-only on the backend (deleteTask) — gate the modal button to match.
  const canDeleteTask = !!editingTask && (isAdmin || editingTask.assignedToUserId === currentUserId)
  // ★ MY WORK scope (replaces the admin-only "Show all firm tasks" checkbox):
  //   mine — my audience (owner / co-assignee / firm-wide) → ?assignedToUserId=me
  //   team — everyone else's work (firm-visible, minus tasks I'm on; client-side)
  //   firm — everything I may see
  // team/firm need the firm view: admin OR seeFirmWideTasks (the SAME resolver
  // the backend list applies — the UI gate is UX, the backend is the gate).
  // Status / priority / case / search / group-by are client-side inside MyWork
  // (the list is fetched across all statuses so "Active" + the collapsed Done
  // group come from one load, and live patches move rows between groups).
  const canSeeFirmTasks = isAdmin || (authUser?.effectivePermissions?.seeFirmWideTasks ?? false)
  const [scope, setScope] = useState<MyWorkScope>('mine')
  // Unclaimed firm-wide — backend filter (?unclaimed=true), decision 1.
  const [unclaimedOnly, setUnclaimedOnly] = useState(false)
  // "Set by me" — tasks I created, including ones I've delegated and am not
  // on (the creator can always view them). Its own fetch (?createdByMe=true).
  const [setByMeOnly, setSetByMeOnly] = useState(false)
  // OVERDUE filter — a SEPARATE toggle, NOT a status option. Applied by
  // MyWork as a CLIENT filter over the current scope's list with the SAME
  // rule the Overdue tile counts (myWorkLogic.urgencyOf → isTaskOverdue:
  // open or in-progress, timed = past the instant, all-day = before today).
  // The dashboard OVERDUE card counts with that same rule over the same
  // "me" audience, so landing here from it shows the card's number.
  // Lazy initial value: TRUE only when the dashboard card set the
  // pendingTasksOverdue preset (cleared on mount so it never sticks).
  const [overdueOnly, setOverdueOnly] = useState<boolean>(
    () => useCasevoStore.getState().pendingTasksOverdue,
  )

  // Dashboard DUE SOON tile hand-off — read once (mount), cleared below.
  const [initialDueSoon] = useState<boolean>(() => useCasevoStore.getState().pendingTasksDueSoon)

  // Local case cache for linking + labels. Same locally-fetched
  // pattern as DocumentSearchView. Future: promote to allCases
  // store slice if a third consumer appears.
  const [allCases, setAllCases] = useState<RawCase[]>([])
  useEffect(() => {
    let cancelled = false
    void fetchCases().then((data) => {
      if (!cancelled) setAllCases(data)
    }).catch((err) => {
      // eslint-disable-next-line no-console
      console.error('[TasksView] Failed to load cases:', err)
    })
    return () => { cancelled = true }
  }, [])

  // Local firm-user cache for the admin "Assigned to" dropdown.
  // Same self-cancelling locally-fetched pattern as allCases.
  // Always-load (no isAdmin gate) — the list is firm-scoped and
  // tiny, and conditional fetching would leave firmUsers stale
  // if the user's role flipped mid-session. Future: promote to
  // a store slice if a second consumer appears (likely candidates:
  // case watchers, mention autocomplete, team sidebar zone).
  // Firm users now come from the shared store slice (loaded once,
  // graceful for non-admins). loadFirmUsers is load-once, so calling it
  // on mount here is cheap if another surface already loaded it.
  const firmUsers = useCasevoStore((s) => s.firmUsers)
  const loadFirmUsers = useCasevoStore((s) => s.loadFirmUsers)
  const getFirmUserById = useCasevoStore((s) => s.getFirmUserById)
  // Who owns the open task (for the status-only notice).
  const editingOwner = editingTask ? getFirmUserById(editingTask.assignedToUserId) : null
  useEffect(() => {
    void loadFirmUsers()
  }, [loadFirmUsers])

  useEffect(() => {
    void loadClients()
  }, [loadClients])

  // ★ SCOPED FETCH + PER-SCOPE CACHE (replaces the plain loadTasks reload).
  // Which server query the current slice needs:
  //   (Overdue is NOT a fetch — audit fix: it's a client filter over the
  //   scope's own list, the same rule the Overdue tile counts with, so the
  //   tile and the filter can never disagree. It used to swap to a self-only
  //   status=open fetch, which showed nothing on Team.)
  //   setByMe   — ?createdByMe=true (tasks I created, whoever they're with).
  //   unclaimed — ?unclaimed=true (firm-wide, nobody beyond the owner).
  //   mine      — ?assignedToUserId=<me> (owner / co-assignee / firm-wide).
  //   firm      — no assignee filter (visibility still applied server-side);
  //               Team AND Firm both use this list, so Team⇄Firm never refetches.
  const fetchKey: TaskFetchKey = setByMeOnly ? 'setByMe' : unclaimedOnly ? 'unclaimed'
    : scope === 'mine' || !canSeeFirmTasks ? 'mine' : 'firm'
  // permissionsVersion in the key: after a live permission change the
  // 'firm' list means something different, so never reuse the old cache.
  const permissionsVersion = useCasevoStore((s) => s.permissionsVersion)
  const cacheKey = `${currentUserId}:${permissionsVersion}:${fetchKey}`
  const cacheKeyRef = useRef(cacheKey)
  cacheKeyRef.current = cacheKey
  const fetchKeyRef = useRef(fetchKey)
  fetchKeyRef.current = fetchKey
  const filtersFor = useCallback((k: TaskFetchKey) => (
    k === 'setByMe' ? { createdByMe: true }
      : k === 'unclaimed' ? { unclaimed: true }
      : k === 'mine' ? { assignedToUserId: currentUserId }
      : {}
  ), [currentUserId])
  // Which cache entry the list currently on screen (store.tasks) belongs to —
  // only ever set when WE put a list there, so a foreign list never gets cached.
  const loadedKeyRef = useRef<string | null>(null)
  // Latest-request-wins guard (a slow earlier scope can't overwrite a newer one).
  const reqSeqRef = useRef(0)
  // Scope-switch loading (B): true only while a scope with NO cache entry loads.
  const [scopeLoading, setScopeLoading] = useState(false)
  const fetchScope = useCallback(async (key: string, k: TaskFetchKey, silent: boolean, retried = false): Promise<void> => {
    const seq = ++reqSeqRef.current
    const startList = useCasevoStore.getState().tasks
    if (!silent) setScopeLoading(true)
    if (startList === null) useCasevoStore.setState({ tasksLoading: true, tasksError: null })
    try {
      const data = await listTasksApi(filtersFor(k))
      if (seq !== reqSeqRef.current) { scopeTaskCache.set(key, data); return } // superseded — still worth caching
      // Silent re-validate: if the on-screen list changed while we were fetching
      // (a quick action / live patch), keep the live list — never clobber it
      // with a snapshot that may predate that change.
      if (silent && useCasevoStore.getState().tasks !== startList) {
        // ★ (Batch C) Don't just DROP the re-validate — the cached list it was
        // meant to correct (e.g. rows deleted / reassigned while this scope
        // was hidden) would then stay stale. Try once more shortly, if we're
        // still on this scope.
        if (!retried) {
          window.setTimeout(() => {
            if (loadedKeyRef.current === key) void fetchScopeRef.current(key, k, true, true)
          }, 1500)
        }
        return
      }
      scopeTaskCache.set(key, data)
      loadedKeyRef.current = key
      useCasevoStore.setState({ tasks: data, tasksLoading: false, tasksError: null })
    } catch (err) {
      if (seq !== reqSeqRef.current || silent) return // a failed background refresh stays silent
      const message = err instanceof Error ? err.message : 'Failed to load tasks'
      console.error('[TasksView] load tasks failed:', err) // eslint-disable-line no-console
      useCasevoStore.setState({ tasksLoading: false, tasksError: message })
    } finally {
      if (seq === reqSeqRef.current) setScopeLoading(false)
    }
  }, [filtersFor])
  // Latest fetchScope for the delayed re-validate retry above.
  const fetchScopeRef = useRef(fetchScope)
  fetchScopeRef.current = fetchScope
  // (D12 live updates) Tasks changed firm-wide (incl. the actor's other tabs,
  // which entity-changed skips) → a silent re-validate of the scope on screen.
  useFirmChange(['tasks', 'cases'], () => { void fetchScopeRef.current(cacheKeyRef.current, fetchKeyRef.current, true) })
  // ★ OWN EDITS leave the list too: entity-changed is echo-suppressed for the
  // actor, so a task I take myself off (or hand to someone else) would linger
  // on Mine until a reload. casevoApi publishes a same-tab change after each
  // task write; once the store row is patched (next tick), drop it if it no
  // longer belongs to the list on screen — it's still under "Set by me".
  useEffect(() => subscribeLocalEntityChanges((e) => {
    if (e.entity !== 'task' || e.op !== 'update') return
    window.setTimeout(() => {
      const st = useCasevoStore.getState()
      const me = st.authUser?.id
      const list = st.tasks
      if (!me || !list) return
      const row = list.find((t) => t.id === e.id)
      if (row && !taskBelongsToScope(row, fetchKeyRef.current, me)) {
        useCasevoStore.setState({ tasks: list.filter((t) => t.id !== e.id) })
      }
    }, 0)
  }), [])
  // ★ LIVE INSERT / REMOVE (mirrors the case list's add/remove-on-assignment).
  // A task that becomes relevant to THIS scope — created for me, reassigned to
  // me, added as co-assignee, shared firm-wide (or, on Unclaimed, un-claimed) —
  // is fetched (GET /api/tasks/:id, access-checked) and INSERTED into the list
  // live; MyWork derives groups / sort / tiles / hero from the list, so they
  // all update. A task that stops belonging (taken off Mine, claimed while on
  // Unclaimed) is REMOVED live. Replaces the old "N new tasks — Refresh" banner.
  //   • scope-safe: belongsToScope() applies the SAME rule as the server query
  //     for the list on screen (mine = owner / co-assignee / firm-wide;
  //     unclaimed = firm-wide with nobody beyond the owner; firm/team = anything
  //     the server delivered to me — the event itself is access-filtered).
  //     Client-side filters (search / priority / case…) are applied by MyWork.
  //   • no dupes: re-checked against the LIVE list at insert time; if a copy is
  //     already there, the newer updatedAt wins (latest-write-wins with the
  //     optimistic quick actions and the live row patches).
  //   • stale-scope safe: an insert is dropped if the scope changed while the
  //     fetch was in flight (the new scope's own load is authoritative).
  //   • cache: the on-screen scope's cache entry follows via the mirror effect;
  //     other cached scopes are silently re-validated when switched to.
  // The actor's own events are echo-suppressed upstream (their own create /
  // edit is already applied locally).
  useEffect(() => {
    const belongsToScope = taskBelongsToScope
    const inFlight = new Set<string>()
    return presence.subscribeEntityChanged((e) => {
      if (e.entity !== 'task' || e.op === 'delete' || e.revoked) return
      const me = useCasevoStore.getState().authUser?.id
      const list = useCasevoStore.getState().tasks
      if (!me || !list) return
      const k = fetchKeyRef.current
      const existing = list.find((t) => t.id === e.id)
      if (existing) {
        // Already listed. Judge scope on the PATCHED row (existing + the
        // event summary) rather than assuming useEntityChangeSync already
        // ran — when this view mounts in the same commit as App (reload onto
        // Tasks, ?taskId deep link) React runs THIS effect first, so
        // `existing` could still be the pre-claim row.
        const patched = { ...existing, ...((e.summary ?? {}) as Partial<Task>) }
        if (e.op === 'update' && !belongsToScope(patched, k, me)) {
          useCasevoStore.setState({ tasks: list.filter((t) => t.id !== e.id) })
        }
        return
      }
      // Not listed — fetch the authoritative row, then insert if it belongs.
      if (inFlight.has(e.id)) return
      inFlight.add(e.id)
      const keyAtStart = cacheKeyRef.current
      void getTaskByIdApi(e.id)
        .then((fresh) => {
          if (cacheKeyRef.current !== keyAtStart) return // scope changed meanwhile
          const cur = useCasevoStore.getState().tasks
          if (!cur || !belongsToScope(fresh, fetchKeyRef.current, me)) return
          setTaskBaseUpdatedAt(fresh.id, fresh.updatedAt)
          setTaskBaseValues(fresh.id, fresh as unknown as Record<string, unknown>)
          const dupe = cur.find((t) => t.id === fresh.id)
          if (dupe) {
            // Latest write wins — never replace a newer row with an older one.
            if (new Date(fresh.updatedAt).getTime() > new Date(dupe.updatedAt).getTime()) {
              useCasevoStore.setState({ tasks: cur.map((t) => (t.id === fresh.id ? fresh : t)) })
            }
            return
          }
          useCasevoStore.setState({ tasks: [...cur, fresh] })
        })
        .catch(() => { /* no access / gone — nothing to insert */ })
        .finally(() => { inFlight.delete(e.id) })
    })
  }, [])
  // Scope / slice change → cached? show it INSTANTLY + silent refresh (C);
  // first visit → keep the current list on screen, dimmed, until the new one
  // arrives (B). The toggle itself flips immediately (it's plain local state).
  useEffect(() => {
    const cached = scopeTaskCache.get(cacheKey)
    if (cached) {
      reqSeqRef.current += 1 // cancel any in-flight first load of another scope
      setScopeLoading(false)
      loadedKeyRef.current = cacheKey
      useCasevoStore.setState({ tasks: cached, tasksLoading: false, tasksError: null })
      void fetchScope(cacheKey, fetchKey, true)
    } else {
      void fetchScope(cacheKey, fetchKey, false)
    }
  }, [cacheKey, fetchKey, fetchScope])
  // Keep the on-screen scope's cache entry in step with live patches, quick
  // actions, creates and deletes (all of which rewrite store.tasks).
  // Reads the LIVE store (not this render's `tasks`), so the commit in which a
  // scope switch swaps the list can never file the previous scope's rows
  // under the new key.
  useEffect(() => {
    const live = useCasevoStore.getState().tasks
    if (live && loadedKeyRef.current) scopeTaskCache.set(loadedKeyRef.current, live)
  }, [tasks])
  // (The "N new tasks — Refresh" banner is gone: remote creates / newly
  // relevant tasks are inserted live by the effect above.)

  // Consume the one-shot dashboard-OVERDUE preset. The initial
  // overdueOnly value was already lazily seeded from this flag above;
  // here we just CLEAR it (mount only) so it doesn't persist into a
  // later manual open of the Tasks view. Guarded so a normal open —
  // where the flag is already false — is a no-op.
  useEffect(() => {
    if (pendingTasksOverdue) {
      setPendingTasksOverdue(false)
    }
    if (useCasevoStore.getState().pendingTasksDueSoon) useCasevoStore.getState().setPendingTasksDueSoon(false)
    // Mount-only: the flag is a navigation handoff, read once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Cross-view "open this task" trigger. CalendarView / the task-email
  // deep-link (and any future producer) set pendingTaskId + showTasks
  // =true to open a SPECIFIC task's detail form. Three-tier resolution
  // so the task ALWAYS opens — and an admin lands straight in it even
  // when it's outside their own-scoped view:
  //
  //   1. LIST-FIRST — if the task is already in the loaded `tasks`,
  //      open it instantly (no fetch, no toggle). The common path.
  //   2. ADMIN AUTO-FIRM-WIDE — a miss for an admin whose view is
  //      own-only (showAll off) flips showAll ON. That re-fetches the
  //      firm-wide list (reloadTasks dep) so the task is now in scope,
  //      and the toggle visibly reflects WHY they can see it.
  //   3. FETCH-BY-ID FALLBACK — regardless of the above (admin whose
  //      firm-wide list is still loading, or any edge), fetch the single
  //      record (getTaskByIdApi) and open it, so the detail opens
  //      immediately and reliably. getTaskById is firm-scoped AND (for
  //      non-admins) assigned-to-them, so this can't leak others' tasks
  //      to a non-admin — a forbidden id 404s and the fetch rejects.
  //
  // openFetchRef dedupes the in-flight fetch so the re-renders from the
  // showAll flip / list reload don't fire it twice. The slot is cleared
  // only once resolved (or on a found/hard-miss), so an admin miss keeps
  // the slot alive across the firm-wide reload.
  const openFetchRef = useRef<string | null>(null)
  useEffect(() => {
    if (!pendingTaskId || tasks === null) return
    // 1. List-first (instant).
    const found = tasks.find((t) => t.id === pendingTaskId)
    if (found) {
      setEditingTask(found)
      setMode('form')
      setPendingTaskId(null)
      openFetchRef.current = null
      return
    }
    // 2. Admin, own-scoped → enable firm-wide (list + toggle catch up).
    if (canSeeFirmTasks && scope === 'mine') {
      setScope('firm')
    }
    // 3. Fetch-by-id fallback — opens the task now, robustly. Dedupe so
    //    the showAll-flip / reload re-renders don't double-fetch.
    if (openFetchRef.current === pendingTaskId) return
    openFetchRef.current = pendingTaskId
    const idToOpen = pendingTaskId
    void getTaskByIdApi(idToOpen)
      .then((task) => {
        setEditingTask(task)
        setMode('form')
      })
      .catch((err) => {
        // Deleted between click and open, or (non-admin) not permitted —
        // don't hang: just clear and stay on the list.
        // eslint-disable-next-line no-console
        console.error('[TasksView] Failed to open task by id:', err)
      })
      .finally(() => {
        setPendingTaskId(null)
        openFetchRef.current = null
      })
  }, [pendingTaskId, tasks, canSeeFirmTasks, scope, setPendingTaskId])

  // Deep-link deadline/task feature — SEPARATE effect for taskIntentId.
  // routeByItemType writes taskIntentId when a task-without-caseId is
  // clicked; we scroll the matching row into view and paint a 2s ring
  // via highlightedTaskId. Distinct from pendingTaskId above — this
  // one keeps the user on the list, doesn't open the form.
  //
  // Runs after tasks are loaded so the row DOM exists. 100ms setTimeout
  // gives React one commit tick to paint the highlight class before we
  // ask the DOM to scroll (matches the pattern in ViewingPane's
  // section-anchor effect). 2000ms timeout clears the ring class.
  //
  // Clears taskIntentId on entry (belt-and-braces against
  // cross-view leak — same discipline as pendingTaskId above).
  useEffect(() => {
    if (!taskIntentId || tasks === null) return
    const targetId = taskIntentId
    // Clear immediately so a subsequent render doesn't re-trigger.
    useCasevoStore.getState().setTaskIntentId(null)
    const scrollTimer = window.setTimeout(() => {
      const el = document.getElementById(`task-row-${targetId}`)
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
      setHighlightedTaskId(targetId)
    }, 100)
    const clearTimer = window.setTimeout(() => {
      setHighlightedTaskId(null)
    }, 2100)  // 100ms delay + 2s ring = 2100ms total
    return () => {
      window.clearTimeout(scrollTimer)
      window.clearTimeout(clearTimer)
    }
  }, [taskIntentId, tasks])

  // Cross-view "new task for this client" trigger. ClientSearchView's
  // per-client Tasks section sets pendingNewTaskClientId + showTasks
  // =true when the user clicks "+ New task" in that section. We flip
  // to create-mode here; the form-seed effect below reads the slot
  // (via closure, NOT via deps — otherwise the form-seed re-fires
  // when the slot is cleared and stomps on the preselection) and
  // applies the preselect + clears the slot. Do NOT clear here.
  useEffect(() => {
    if (!pendingNewTaskClientId) return
    setEditingTask(null)
    setMode('form')
  }, [pendingNewTaskClientId])

  // Cross-view "new task on this DAY" trigger (Calendar day-panel).
  // Sets pendingNewTaskDueDate + showTasks=true; here we flip to
  // create-mode, and the form-seed effect below reads the slot via
  // closure to pre-fill the due-date, then clears it. Same discipline
  // as the client slot above — do NOT clear here.
  useEffect(() => {
    if (!pendingNewTaskDueDate) return
    setEditingTask(null)
    setMode('form')
  }, [pendingNewTaskDueDate])

  // ★ MY WORK side data — refreshed on open and (debounced) whenever the task
  // list changes (create / complete / claim / live patch), so the tiles + rails
  // stay in step with the queue.
  //   summary  — GET /api/tasks/summary (done this week + gated team load)
  //   calendar — the EXISTING GET /api/calendar feed for TODAY, scope 'mine'
  //              (decision 3: the rail is a bridge, no new endpoint)
  const [summary, setSummary] = useState<TaskSummary | null>(null)
  const [todayEntries, setTodayEntries] = useState<CalendarEntry[] | null>(null)
  useEffect(() => {
    if (mode !== 'list') return
    const t = window.setTimeout(() => {
      const now = new Date()
      const dayStart = startOfLocalDay(now)
      // ±1 day around the local day: all-day items sit at midnight UTC, which
      // can fall outside [local 00:00, 23:59] in some timezones. MyWork then
      // keeps exactly the entries whose calendar day is today.
      const winStart = new Date(dayStart.getTime() - 86400000)
      const winEnd = new Date(dayStart.getTime() + 2 * 86400000 - 1)
      void getTaskSummaryApi({ weekStart: startOfLocalWeek(now).toISOString(), today: ymdLocal(now) })
        .then(setSummary)
        .catch((err) => { console.error('[TasksView] summary failed:', err) }) // eslint-disable-line no-console
      void fetchCalendarApi({ from: winStart.toISOString(), to: winEnd.toISOString(), scope: 'mine' })
        .then(setTodayEntries)
        .catch((err) => { setTodayEntries([]); console.error('[TasksView] calendar rail failed:', err) }) // eslint-disable-line no-console
    }, summary === null ? 0 : 800)
    return () => window.clearTimeout(t)
    // summary intentionally read only for the first-load (no-delay) decision.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks, mode])

  // ★ TEAM-LOAD periodic refresh. Other people's task changes don't reach the
  // summary above (it only re-fetches when THIS viewer's loaded list changes),
  // so the team-load panel re-polls GET /api/tasks/summary every 30s — silently
  // (no dim / spinner; setSummary only when the payload actually changed, so an
  // unchanged poll doesn't even re-render). Runs ONLY while the panel is on
  // screen: My work list mode, not the calendar-opened modal / an opening
  // deep-link, the viewer may see firm tasks, and the server returned team data
  // (MyWork renders the panel iff summary.team — MyWork.tsx:845). Paused while
  // the tab is hidden (visibilitychange); on return it refreshes at once if a
  // poll was missed, then resumes the 30s cadence. Cleared on unmount / leave.
  const teamPanelShown = mode === 'list' && !openedFromCalendar && pendingTaskId === null
    && canSeeFirmTasks && !!summary?.team
  useEffect(() => {
    if (!teamPanelShown) return
    let timer: number | null = null
    let lastRun = Date.now()
    let cancelled = false
    const poll = (): void => {
      lastRun = Date.now()
      const now = new Date()
      void getTaskSummaryApi({ weekStart: startOfLocalWeek(now).toISOString(), today: ymdLocal(now) })
        .then((next) => {
          if (cancelled) return
          setSummary((prev) => (prev && JSON.stringify(prev) === JSON.stringify(next) ? prev : next))
        })
        .catch(() => { /* silent — the next tick retries */ })
    }
    const start = (): void => {
      if (timer !== null) return
      timer = window.setInterval(poll, TEAM_LOAD_POLL_MS)
    }
    const stop = (): void => {
      if (timer !== null) { window.clearInterval(timer); timer = null }
    }
    const onVisibility = (): void => {
      if (document.hidden) { stop(); return }
      if (Date.now() - lastRun >= TEAM_LOAD_POLL_MS) poll()
      start()
    }
    if (!document.hidden) start()
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      cancelled = true
      stop()
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [teamPanelShown])

  // Case display info for My work rows: the shared case name, THE reference
  // (SURNAME-YEAR-ID), client. A case outside allCases (the viewer can't access
  // it) shows only its id segment — the claimant surname is never revealed.
  const caseInfoFor = useCallback((caseId: string | null) => {
    if (!caseId) return undefined
    const c = allCases.find((x) => x.id === caseId)
    if (!c) return { name: 'Case', ref: caseShortId(caseId) }
    const client = caseClientName(c)
    return { name: caseName(c), ref: caseRef(c), client: client || undefined }
  }, [allCases])

  // Case + client labels for row badges.
  const caseLabelFor = (caseId: string | null): string | undefined => {
    if (!caseId) return undefined
    const c = allCases.find((x) => x.id === caseId)
    if (!c) return undefined
    return caseNameWithRef(c)
  }
  const clientLabelFor = (clientId: string | null): string | undefined => {
    if (!clientId) return undefined
    const c = clients.find((x) => x.id === clientId)
    if (!c) return undefined
    return `${c.forename} ${c.surname}`.trim() || '(unnamed)'
  }
  // Due date + time → the stored dueDate: utils/taskDue.ts combineDue.
  // Commit a due date/time change in EDIT mode: update both bits of state
  // + autosave the combined instant. Used by the date + time inputs so
  // either edit persists the whole dueDate.
  // DateInput never reports a half-typed date, so nextDate === null here is a
  // deliberate clear — a half-typed edit can't wipe the saved due date.
  const commitDue = (nextDate: string | null, nextTime: string | null): void => {
    setFormDueDate(nextDate)
    setFormDueTime(nextTime)
    autosaveEditField({ dueDate: combineDue(nextDate, nextTime) })
  }

  // A task row's × is shown ONLY for done tasks AND only when the
  // current user has the right to delete it. Same canEditFully-style
  // rule as the form gating: admins can dismiss anything done;
  // workers can only dismiss tasks they created themselves.
  const canDismissRow = (task: Task): boolean => {
    if (task.status !== 'done') return false
    if (isAdmin) return true
    // The backend deletes for the OWNER (deleteTask: assignedToUserId = caller).
    return task.assignedToUserId === currentUserId
  }
  const handleDismissRow = async (task: Task): Promise<void> => {
    try {
      await deleteTask(task.id)
    } catch (err) {
      // Failure isn't fatal — log and let the user retry via the form
      // delete path. No toast for now (no global toast system in app).
      // eslint-disable-next-line no-console
      console.error('[TasksView] Failed to dismiss task:', err)
    }
  }

  // ── NAV HANDLERS ─────────────────────────────────────────
  // Close the task form and route back to wherever it was opened FROM.
  // Shared by Cancel/Back (form mode) AND by a successful create/save
  // (handleSubmit), so a task opened from the Calendar (or ClientSearch
  // / Dashboard) returns THERE after creating instead of stranding the
  // user on the tasks list. taskOpenedFrom=null (opened from the tasks
  // list itself) → just close the form to the list, unchanged. The slot
  // is cleared so the next list-opened form behaves normally.
  const closeFormToOrigin = (): void => {
    // Read the LIVE origin from the store (not the render closure) so a
    // stale closure can never mask a 'calendar' origin at exit time.
    const store = useCasevoStore.getState()
    const origin = store.taskOpenedFrom
    setMode('list')
    setEditingTask(null)
    if (origin === 'calendar' && store.showCalendar) {
      // The task is a MODAL over the still-mounted calendar (no history
      // step was added for it) — closing it just reveals the calendar.
      store.setShowTasks(false)
      store.setTaskOpenedFrom(null)
      return
    }
    store.setTaskOpenedFrom(null)
    if (origin) {
      // Opened from another screen (client page, dashboard…): that was a
      // navigation, so return to the place you were actually on. No
      // history (opened fresh from a link) → the origin screen.
      goBack(origin === 'clientSearch' ? { view: 'clientSearch' } : origin === 'calendar' ? { view: 'calendar' } : { view: 'dashboard' })
    }
    // No upstream screen — stay on the Tasks list.
  }
  // Diary batch (task→case click-through): open the task's linked case
  // from the form. caseOpenedFrom='taskList' so ViewingPane's Back returns
  // to the Tasks view. Close Tasks BEFORE the await so ViewingPane mounts
  // + shows its own spinner (instant-open, mirrors CalendarView).
  const handleOpenLinkedCase = async (caseId: string): Promise<void> => {
    // navigateTo drops every takeover (incl. a calendar mounted behind a
    // calendar-opened task) and records the step — Back returns here.
    try {
      await navigateTo({ view: 'caseDetail', caseId }, { intents: (s) => s.setCaseOpenedFrom('taskList') })
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[TasksView] Failed to open linked case:', err)
    }
  }
  // Task→client click-through — mirrors handleOpenLinkedCase. ClientSearchView
  // consumes pendingClientId to open that client's detail; pushViewHistory so
  // browser Back returns to the Tasks view.
  const handleOpenLinkedClient = (clientId: string): void => {
    void navigateTo({ view: 'clientDetail', clientId })
  }
  const handleBack = (): void => {
    if (mode === 'form') {
      // Opened from Calendar / ClientSearch / Dashboard → return there;
      // otherwise just close the form to the list.
      requestCloseForm()
      return
    }
    // LIST mode: the place you were on before Tasks (App.tsx popstate
    // flushes + restores it); no history → the dashboard.
    goBack({ view: 'dashboard' })
  }
  const handleHome = (): void => {
    void navigateTo({ view: 'caseList' })
  }

  // ── FORM STATE ───────────────────────────────────────────
  const [formTitle, setFormTitle] = useState('')
  const [formDescription, setFormDescription] = useState('')
  // Which task formDescription was last seeded for. On the FIRST render of a
  // newly opened task the seeding effect hasn't run yet (formDescription is
  // still the previous/blank value) — reading it then made the view take the
  // slow live-doc path for a frame (+ open a socket) even when the task had a
  // stored note. Until seeded, the view reads editingTask.description directly.
  const seededTaskIdRef = useRef<string | null>(null)
  const viewDescription = editingTask && seededTaskIdRef.current !== editingTask.id
    ? (editingTask.description ?? '')
    : formDescription
  // ★ The REAL cause of the slow note: `taskView` is state that the seeding
  // effect sets to 'view' only AFTER the first render — and it starts as 'edit'
  // (useState default / left over from the last task). So on every open the
  // modal first rendered the EDIT branch, which mounts the live collab Notes
  // editor (a WebSocket connect + auth + DB load + sync), then flipped to view
  // and tore it down. Until the seed runs for THIS task, render VIEW directly.
  // + a user who can't fully edit an existing task is ALWAYS in VIEW, whatever
  // path set taskView (so the edit form can never open for a non-editor).
  const shownTaskView: 'view' | 'edit' =
    editingTask && (seededTaskIdRef.current !== editingTask.id || !canEditFully) ? 'view' : taskView
  const [formDueDate, setFormDueDate] = useState<string | null>(null)
  // Diary click-to-add: optional 'HH:mm' due TIME. '' / null = whole-day
  // (dueDate stays date-only → midnight UTC → renders in the all-day strip).
  // When set, the create/edit combines date+time into a full instant so the
  // task lands on its hour slot in the time-grid.
  const [formDueTime, setFormDueTime] = useState<string | null>(null)
  const [formStatus, setFormStatus] = useState<TaskStatus>('open')
  const [formPriority, setFormPriority] = useState<TaskPriority>('normal')
  // ★ ASSIGN TO — one ordered list: the LEAD (formAssigneeId → assignedToUserId)
  // then the co-assignees (formAudienceIds → TaskAssignee rows), plus the
  // firm-wide flag. Gated by the resolved effective permissions (UX only — the
  // backend diff-gate is the real gate). See components/tasks/TaskAssigneesField.
  const [formFirmWide, setFormFirmWide] = useState(false)
  const [formAudienceIds, setFormAudienceIds] = useState<string[]>([])
  const canAssignTasksToOthers = authUser?.effectivePermissions?.assignTasksToOthers ?? false
  const canCreateFirmWideTasks = authUser?.effectivePermissions?.createFirmWideTasks ?? false
  const [formAssigneeId, setFormAssigneeId] = useState<string>('')
  // Lead changes: admin, the current lead or the task's creator (the backend
  // enforces the same in taskController.updateTask). A new task: you.
  const canChangeLead = !editingTask || isAdmin || editingTask.assignedToUserId === currentUserId || editingTask.createdByUserId === currentUserId
  const formAssign: TaskAssignState = { leadId: formAssigneeId, others: formAudienceIds, firmWide: formFirmWide }
  const [formLinkType, setFormLinkType] = useState<'none' | 'case' | 'client'>('none')
  const [formCaseId, setFormCaseId] = useState<string>('')
  const [formClientId, setFormClientId] = useState<string>('')
  const [formError, setFormError] = useState<string | null>(null)
  const [formSubmitting, setFormSubmitting] = useState(false)
  const [titleError, setTitleError] = useState<string | null>(null)
  const [showClientPicker, setShowClientPicker] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  // Seed form from editingTask when entering edit mode; clear when
  // entering create mode. The create-mode branch checks the cross-
  // view pendingNewTaskClientId slot via CLOSURE — deliberately NOT
  // in the deps array — so clearing the slot doesn't re-fire the
  // effect and overwrite the preselection on the next render. The
  // companion consumer effect above flips mode→'form' when the slot
  // is set; this effect picks up that mode change and reads the slot
  // value from the SAME render's closure, then clears it.
  useEffect(() => {
    // Leaving the form → forget the seed so a re-open of the SAME task starts
    // from the synchronous "open in VIEW" default below, not stale state.
    if (mode !== 'form') seededTaskIdRef.current = null
    if (mode === 'form') {
      // Existing task → open read-only VIEW; brand-new task → straight to EDIT.
      setTaskView(editingTask ? 'view' : 'edit')
      if (editingTask) {
        setFormTitle(editingTask.title)
        setFormDescription(editingTask.description ?? '')
        seededTaskIdRef.current = editingTask.id
        setFormDueDate(editingTask.dueDate ? editingTask.dueDate.slice(0, 10) : null)
        // Diary: derive the due TIME for the time input. A whole-day task is
        // stored at midnight UTC → show no time (''); a timed task shows its
        // LOCAL HH:mm (matching how the time-grid places it by local hour).
        if (editingTask.dueDate) {
          const dd = new Date(editingTask.dueDate)
          const isTimed = dd.getUTCHours() !== 0 || dd.getUTCMinutes() !== 0
          setFormDueTime(
            isTimed
              ? `${String(dd.getHours()).padStart(2, '0')}:${String(dd.getMinutes()).padStart(2, '0')}`
              : null,
          )
        } else {
          setFormDueTime(null)
        }
        setFormStatus(editingTask.status)
        setFormPriority(normalisePriority(editingTask.priority))
        {
          const a = assignStateOf(editingTask)
          setFormAudienceIds(a.others)
          setFormFirmWide(a.firmWide)
        }
        setFormAssigneeId(editingTask.assignedToUserId)
        // Seed the optimistic-concurrency base for per-field autosave — the
        // server updatedAt this task had when the edit form opened. GUARD:
        // never REGRESS an already-advanced base to an older row value (a
        // receiver's row may briefly be stale). First open (no base) → seed
        // from the row; otherwise seed = max(existing base, row updatedAt).
        // ISO-8601 UTC strings compare lexically == chronologically.
        const existingTaskBase = getTaskBaseUpdatedAt(editingTask.id)
        const seedBase = !existingTaskBase || editingTask.updatedAt >= existingTaskBase
          ? editingTask.updatedAt
          : existingTaskBase
        setTaskBaseUpdatedAt(editingTask.id, seedBase)
        // Seed the column-aware base VALUES from the row the form opened on, so
        // a 409 can tell a cross-column edit (silent re-apply) from a same-column
        // clash (toast). Uses editingTask (the store row) as the "what I started
        // from" snapshot.
        setTaskBaseValues(editingTask.id, editingTask as unknown as Record<string, unknown>)
        if (editingTask.caseId) {
          setFormLinkType('case')
          setFormCaseId(editingTask.caseId)
          setFormClientId('')
        } else if (editingTask.clientId) {
          setFormLinkType('client')
          setFormClientId(editingTask.clientId)
          setFormCaseId('')
        } else {
          setFormLinkType('none')
          setFormCaseId('')
          setFormClientId('')
        }
      } else {
        setFormTitle('')
        setFormDescription('')
        seededTaskIdRef.current = null
        // Seed the due-date from the Calendar day-panel slot (a
        // 'YYYY-MM-DD' string) when present, else blank. Read via
        // closure + cleared here so it doesn't persist to the next
        // list-opened create form.
        if (pendingNewTaskDueDate) {
          setFormDueDate(pendingNewTaskDueDate)
          setPendingNewTaskDueDate(null)
        } else {
          setFormDueDate(null)
        }
        // Diary click-to-add: seed the due TIME from the slot (an hour-slot
        // click sets 'HH:mm'; an all-day click / day-panel leaves it null),
        // then clear it so the next list-opened create form is whole-day.
        if (pendingNewTaskDueTime) {
          setFormDueTime(pendingNewTaskDueTime)
          setPendingNewTaskDueTime(null)
        } else {
          setFormDueTime(null)
        }
        setFormStatus('open')
        setFormPriority('normal')
        // A new task: you as lead, nobody else, not firm-wide.
        setFormFirmWide(false)
        setFormAudienceIds([])
        setFormAssigneeId(currentUserId)
        if (pendingNewTaskClientId) {
          // Cross-view entry from ClientSearchView's "+ New task" —
          // preselect the client and clear the slot.
          setFormLinkType('client')
          setFormCaseId('')
          setFormClientId(pendingNewTaskClientId)
          setPendingNewTaskClientId(null)
        } else {
          setFormLinkType('none')
          setFormCaseId('')
          setFormClientId('')
        }
      }
      setFormError(null)
      setTitleError(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, editingTask])

  // Unsaved-changes guard — CREATE mode only (an existing task autosaves per
  // field): a new task with anything changed from its opening defaults.
  const createDirty = useDraftDirty(mode === 'form' && !editingTask, {
    formTitle, formDescription, formDueDate, formDueTime, formStatus, formPriority, formFirmWide,
    formAudienceIds, formAssigneeId, formLinkType, formCaseId, formClientId,
  })
  const guardLeave = useUnsavedChangesGuard(createDirty && !formSubmitting)
  // ×, Cancel and ← Back on the form: ask before dropping a new task's draft.
  const requestCloseForm = (): void => guardLeave(closeFormToOrigin)

  const handleNewTask = (): void => {
    setEditingTask(null)
    setMode('form')
  }
  const handleEditTask = (task: Task): void => {
    setEditingTask(task)
    setMode('form')
  }

  // EDIT-mode per-field autosave. Every editable field's onChange calls this
  // (in addition to its setFormX) so the task saves as you go — mirroring
  // witnesses/deadlines. No-op in CREATE mode (no id yet): new tasks stay
  // commit-on-submit via handleSubmit's createTask path below.
  const autosaveEditField = (changes: Partial<CreateTaskPayload>): void => {
    if (editingTask) scheduleTaskSave(editingTask.id, changes)
  }
  // Assign-to change → local state + (EDIT mode) save at once, like Status.
  // Sends ONLY the fields this user may change (assignPayload), so the
  // backend's diff-gate is never tripped by an un-permitted field. A lead
  // change is broadcast on the assigned-to live key.
  const commitAssign = (next: TaskAssignState): void => {
    const leadChanged = next.leadId !== formAssigneeId
    setFormAssigneeId(next.leadId)
    setFormAudienceIds(next.others)
    setFormFirmWide(next.firmWide)
    if (editingTask) {
      autosaveEditField(assignPayload(next, canAssignTasksToOthers, canCreateFirmWideTasks))
      void flushTaskAutoSave().then(() => { if (leadChanged) sendTaskFieldNow(editingTask.id, 'task.assigned-to') }).catch(() => {})
    }
  }
  // Removing someone who'd be left unable to see the task → ask first. They
  // keep it while they're the lead / still listed, the task is firm-wide,
  // they're an admin, or they CREATED it (the creator can always view it). Other
  // people's "See colleagues' tasks" isn't known here — hence "may".
  const assignLossWarning = (userId: string, next: TaskAssignState): string | null => {
    if (next.firmWide || next.leadId === userId || next.others.includes(userId)) return null
    if (userId === currentUserId) {
      // The creator can always view a task they set (a new task: you are the
      // creator) — taking yourself off never hides it from you.
      if (canSeeFirmTasks || !editingTask || editingTask.createdByUserId === currentUserId) return null
      return 'You won’t be on this task any more, so you may lose access to it.'
    }
    const u = getFirmUserById(userId)
    if (u?.role === 'admin' || editingTask?.createdByUserId === userId) return null
    const name = u ? (`${u.firstName} ${u.lastName}`.trim() || u.email) : 'They'
    return `${name} won’t be on this task any more, so they may lose access to it (unless they can see colleagues’ tasks).`
  }

  // ── Stage 1e: live edit-sync for the task edit form ───────────────
  // Mirrors client notes: the values live in LOCAL form state, so tasks are
  // handled here (not the central engine). Room task:<id> (joined above via
  // usePresenceRoom) scopes incoming edits to the open task, so the fieldKey
  // carries no id.
  //   SEND (field blur): flush the per-field autosave so the value persists and
  //     the task base advances to the new server updatedAt, then broadcast the
  //     store value + updatedAt.
  //   RECEIVE: apply to BOTH the store (applyRemoteTaskEdit → tasks[] + base)
  //     AND the open form's local state (the form setters), unless it's the
  //     field the local user currently has focused (caret clobber).
  // Degrades gracefully (sendFieldEdit no-ops when the socket is down).
  const taskLastSentRef = useRef<Record<string, string>>({})
  // Per-fieldKey typing-debounce timers for the near-live "watch as you type"
  // broadcast (title/description only). Keyed so only the field being typed in
  // is ever scheduled.
  const taskInputTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({})
  const clearTaskInputTimer = (key: string): void => {
    const t = taskInputTimersRef.current[key]
    if (t) { clearTimeout(t); delete taskInputTimersRef.current[key] }
  }
  // Broadcast ONE task fieldKey's current persisted value (read from the store
  // task + the row's advanced base). Assumes the autosave has already flushed
  // (so the store value + base are current). Deduped per key so re-sending an
  // unchanged field is a no-op. Shared by the blur send and the Save-changes
  // broadcast.
  const sendTaskFieldNow = (taskId: string, key: string): void => {
    const field = TASK_KEY_TO_FIELD[key]
    if (!field) return
    const t = useCasevoStore.getState().tasks?.find((x) => x.id === taskId)
    if (!t) return
    const updatedAt = getTaskBaseUpdatedAt(taskId)
    if (!updatedAt) return
    const value = (t as unknown as Record<string, unknown>)[field]
    const serial = `${updatedAt}::${JSON.stringify(value ?? null)}`
    if (taskLastSentRef.current[key] === serial) return
    taskLastSentRef.current[key] = serial
    presence.sendFieldEdit(key, value, updatedAt)
  }
  const handleTaskFieldBlur = (e: React.FocusEvent<HTMLDivElement>): void => {
    const el = (e.target as HTMLElement | null)?.closest('[data-field]') as HTMLElement | null
    const key = el?.dataset.field
    if (!key || !editingTask) return
    if (!TASK_KEY_TO_FIELD[key]) return
    const taskId = editingTask.id
    // Blur sends the final value now — drop any pending typing broadcast for
    // this key so it can't fire a redundant flush+send right after (the
    // sendTaskFieldNow dedupe suppresses a duplicate SEND; this also avoids a
    // duplicate flush/save).
    clearTaskInputTimer(key)
    void (async () => {
      await flushTaskAutoSave()
      sendTaskFieldNow(taskId, key)
    })()
  }
  // ── SEND (debounced typing → save → broadcast) for tasks ──────────
  // Tasks live in LOCAL form state + their own per-field autosave, so they sit
  // outside the central flushReadBroadcast. This mirrors that engine's generic
  // input listener: on typing in a free-text task field (title/description), a
  // ~400ms per-key debounce flushes the task autosave (persist → base advances)
  // then broadcasts the field — so watchers see the task update near-live, not
  // only on blur. Deduped by sendTaskFieldNow; focus-excluded on receive (the
  // typist isn't clobbered). NOT co-editing — last-writer-wins on blur as today.
  const handleTaskFieldInput = (e: React.FormEvent<HTMLDivElement>): void => {
    const el = (e.target as HTMLElement | null)?.closest('[data-field]') as HTMLElement | null
    const key = el?.dataset.field
    if (!key || !editingTask) return
    if (!TASK_TEXT_KEYS.has(key)) return
    const taskId = editingTask.id
    clearTaskInputTimer(key)
    taskInputTimersRef.current[key] = setTimeout(() => {
      delete taskInputTimersRef.current[key]
      void (async () => {
        await flushTaskAutoSave()
        sendTaskFieldNow(taskId, key)
      })()
    }, TASK_INPUT_DEBOUNCE_MS)
  }
  // Clear any pending typing-broadcast timers on unmount.
  useEffect(() => {
    const timers = taskInputTimersRef.current
    return () => { for (const t of Object.values(timers)) clearTimeout(t) }
  }, [])
  useEffect(() => {
    const unsubscribe = presence.subscribeFieldEdits((edit) => {
      const field = TASK_KEY_TO_FIELD[edit.fieldKey]
      if (!field) return
      // Don't clobber the field the local user is editing.
      if (presence.getLocalFocusedField() === edit.fieldKey) return
      if (!editingTask) return
      // Store + base (no loop — applyRemoteTaskEdit doesn't schedule a save).
      applyRemoteTaskEdit(editingTask.id, { [field]: edit.value } as Partial<Task>, edit.updatedAt)
      // Reflect into the open form's LOCAL state so the visible editor updates.
      switch (field) {
        case 'title':
          setFormTitle(typeof edit.value === 'string' ? edit.value : String(edit.value ?? ''))
          break
        case 'description':
          setFormDescription(typeof edit.value === 'string' ? edit.value : String(edit.value ?? ''))
          break
        case 'dueDate':
          setFormDueDate(typeof edit.value === 'string' && edit.value ? edit.value.slice(0, 10) : null)
          break
        case 'status':
          setFormStatus(edit.value as TaskStatus)
          break
        case 'priority':
          setFormPriority(normalisePriority(edit.value))
          break
        case 'assignedToUserId':
          setFormAssigneeId(typeof edit.value === 'string' ? edit.value : String(edit.value ?? ''))
          break
        default:
          break
      }
    })
    // FAST TEXT for the task form: the task VALUES live in LOCAL form state (not a
    // store), so the app-level useRemoteCaretTextApply can't reach them. Apply the
    // caret message's full value straight into the visible form field so task text
    // keeps pace with the remote cursor (title is the only eligible cursor field —
    // description is a Yjs editor). Guarded: same open task, not the field the
    // local user is editing. The near-live receiver above stays the store/base
    // backstop.
    const unsubCaret = presence.subscribeFieldCaret((caret) => {
      const field = TASK_KEY_TO_FIELD[caret.fieldKey]
      if (!field || !editingTask) return
      if (presence.getLocalFocusedField() === caret.fieldKey) return
      if (field === 'title') setFormTitle(caret.caretText)
    })
    return () => { unsubscribe(); unsubCaret() }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingTask?.id])

  // VIEW-mode "Mark done" / "Reopen" — flips status WITHOUT opening the edit
  // form. Reuses the per-field autosave (+ flush + live broadcast) so it
  // persists exactly like an in-form status change. The VIEW renders off
  // formStatus, so no editingTask mutation is needed (which would re-run the
  // heavy seed effect). Always allowed — status is the one field a worker may
  // change even on a task assigned to them by someone else.
  const handleToggleDone = async (): Promise<void> => {
    await handleSetStatus(formStatus === 'done' ? 'open' : 'done')
  }
  // Set ANY status (Open / In progress / Done) — the view's status control for
  // status-users. Same save path as Mark done: a status-only PATCH through the
  // per-task autosave (so the concurrency base + live broadcast are unchanged);
  // the backend accepts status alone even from a firm-wide-only participant.
  const handleSetStatus = async (next: TaskStatus): Promise<void> => {
    if (!editingTask || next === formStatus) return
    setFormStatus(next)
    autosaveEditField({ status: next })
    try {
      await flushTaskAutoSave()
      sendTaskFieldNow(editingTask.id, 'task.status')
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[TasksView] Failed to toggle task status:', err)
    }
  }

  const handleSubmit = async (): Promise<void> => {
    const trimmed = formTitle.trim()
    if (!trimmed) {
      setTitleError('Title is required')
      return
    }
    setTitleError(null)
    setFormError(null)
    setFormSubmitting(true)
    try {
      if (editingTask) {
        // Edit mode is AUTOSAVED per field — the button just flushes any
        // pending debounced change, then navigates. No re-commit here (which
        // would bypass the concurrency base and double-save).
        // ★ First fire the Notes editor's pending (≤1.5s debounced) HTML
        // snapshot so the just-typed note joins THIS flush — otherwise a note
        // typed right before Save never reached task.description and the view
        // showed "No notes." (see RichTextEditor flushCollabSnapshots).
        flushCollabSnapshots()
        await flushTaskAutoSave()
        // Belt-and-braces live-sync: broadcast ALL synced fields so pressing
        // "Save changes" syncs live even if the final edit never fired a blur
        // (e.g. typed the title then clicked Save directly). Deduped per key,
        // so fields already broadcast on blur don't re-send.
        for (const key of Object.keys(TASK_KEY_TO_FIELD)) {
          sendTaskFieldNow(editingTask.id, key)
        }
      } else {
        // Create mode: commit-on-submit (unchanged — no id to autosave yet).
        const payload: CreateTaskPayload = {
          title: trimmed,
          description: formDescription.trim() || null,
          dueDate: combineDue(formDueDate, formDueTime),
          status: formStatus,
          priority: formPriority,
          ...assignPayload(formAssign, canAssignTasksToOthers, canCreateFirmWideTasks),
          caseId: formLinkType === 'case' ? (formCaseId || null) : null,
          clientId: formLinkType === 'client' ? (formClientId || null) : null,
        }
        await createTask(payload)
        // (Stage 3) No client-side list-update broadcast on create — the old
        // relay's receiver only patched EXISTING rows (never inserted), so task
        // CREATE was never live-inserted anyway; firm-wide create live-insert is
        // Stage 4. The legacy relay is retired.
      }
      // Return to wherever the form was opened FROM (calendar / client /
      // dashboard), matching Cancel/Back. A calendar-launched create thus
      // lands the user back on the CALENDAR after saving; a form opened
      // from the tasks list itself (taskOpenedFrom=null) just closes to
      // the list, unchanged.
      closeFormToOrigin()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save task'
      setFormError(message)
    } finally {
      setFormSubmitting(false)
    }
  }

  const handleDelete = async (): Promise<void> => {
    if (!editingTask) return
    setShowDeleteConfirm(false)
    setFormSubmitting(true)
    try {
      // Drop any pending autosave for this task so we don't PATCH a row we're
      // about to delete.
      cancelTaskSave(editingTask.id)
      await deleteTask(editingTask.id)
      // Return to wherever the task was opened FROM (calendar / client /
      // dashboard), matching Save/Back — a calendar-opened task deleted
      // here returns to the CALENDAR, not the tasks list.
      closeFormToOrigin()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete task'
      setFormError(message)
    } finally {
      setFormSubmitting(false)
    }
  }

  // ★ My work quick actions (inline complete / reprioritise / reassign / claim)
  // are OPTIMISTIC: the row is patched in the store the moment the user acts
  // (so the queue, tiles, chips and scope membership move instantly), then the
  // SAME task PATCH as the modal (updateTaskApi — the audience/assignee +
  // status + priority save) runs in the background. Previously the UI waited
  // for the full PATCH round-trip (store.updateTask awaited updateTaskApi
  // before touching the row), which is what made reassign feel slow.
  //   • Per-task op chain: rapid clicks (e.g. ticking several people) are sent
  //     IN ORDER, so an older save can never land after a newer one.
  //   • Only the LATEST op's server response is applied (no flash-back to an
  //     intermediate state while later clicks are still in flight).
  //   • Failure → re-read the task from the server (authoritative revert) and
  //     rethrow so MyWork shows the error toast.
  // On success the concurrency base advances (no spurious 409 in the modal) and
  // the server's op:update broadcast live-patches every other open list.
  // The engine now lives in components/tasks/taskActions.ts (quickUpdateTask)
  // so the client Tasks tab runs the IDENTICAL optimistic save + shares the
  // per-task chain — a task ticked there moves here too (and vice versa).
  const handleQuickUpdate = useCallback((id: string, payload: UpdateTaskPayload): Promise<Task> => quickUpdateTask(id, payload), [])
  // Rail "Open calendar" / calendar entries → the calendar (Back returns here).
  const handleOpenCalendar = (): void => {
    void navigateTo({ view: 'calendar' })
  }
  // While a SPECIFIC task is being opened (pendingTaskId set — e.g. a
  // calendar/email deep-link) and the form isn't up yet, we're in an
  // "opening" state: the consumer effect is finding it in the list or
  // fetching it by id + flipping showAll. Render a clean loading
  // placeholder in that window rather than the tasks LIST, so opening a
  // firm-wide / fetch-by-id task never flashes the wrong list. Resolves
  // to mode==='form' on success, or (on a failed/again-cleared open)
  // pendingTaskId becomes null → the list shows normally.
  const isOpeningTask = pendingTaskId !== null && mode !== 'form'
  // A calendar-opened task NEVER shows the tasks list — the flow always
  // resolves to the form (open OR create). Until mode flips to 'form', show the
  // opening overlay (dimming the calendar) instead of flashing the list.
  const showOpeningState = isOpeningTask || (openedFromCalendar && mode !== 'form')
  const showsTaskList = mode === 'list' && !isOpeningTask && !openedFromCalendar

  // ── FORM-MODE hero-band derived display (view + edit) ─────────────
  const isDone = formStatus === 'done'
  const dueIso = editingTask?.dueDate ?? null
  const dueHasTime = (() => {
    if (!dueIso) return false
    const d = new Date(dueIso)
    return d.getUTCHours() !== 0 || d.getUTCMinutes() !== 0
  })()
  const dueDisplay = !dueIso
    ? 'No due date'
    : dueHasTime ? fmtInstant(dueIso) : fmtYmd(dueIso.slice(0, 10))
  // Overdue = has a due date, not done, and the due moment has passed. Timed
  // tasks compare the instant; all-day tasks are overdue only once their whole
  // day is in the past (local calendar date).
  const isOverdue = (() => {
    if (!dueIso || isDone) return false
    if (dueHasTime) return new Date(dueIso).getTime() < Date.now()
    const now = new Date()
    const todayYmd = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
    return dueIso.slice(0, 10) < todayYmd
  })()
  const statusPillCls = isDone
    ? 'bg-[#74c69d] text-[#0d2a1a]'
    : formStatus === 'in_progress' ? 'bg-[#e9a23b] text-[#3a2a08]' : 'bg-white/20 text-white'
  const statusLabel = isDone ? 'Done' : formStatus === 'in_progress' ? 'In progress' : 'To do'
  // PRIORITY pill on the green hero band (sits after the status pill). Tuned
  // for the forest band in BOTH themes (the band is always dark green): High =
  // the dashboard's overdue coral (#f28b7d) — severity, not gold, and distinct
  // from In-progress amber; Normal = neutral white/15; Low = quiet outline.
  const priorityHeroCls = formPriority === 'high'
    ? 'bg-[#f28b7d] text-[#3a0f09]'
    : formPriority === 'low' ? 'border border-white/35 text-white/80' : 'bg-white/15 text-white/90'

  // Shared green hero band (view + edit). `editable` swaps the static title for
  // an input in the band (kept as data-field="task.title" so the near-live
  // relay + focus ownership still fire). Type pill + STATUS pill on both.
  const taskHeroBand = (editable: boolean): React.ReactNode => (
    <div className="relative px-6 sm:px-8 py-6 text-white bg-gradient-to-br from-casevo-forest to-casevo-brand dark:from-[#1a4230] dark:to-[#2d6a4f]">
      <div className="absolute top-4 right-4 flex items-center gap-1.5">
        {editingTask && (
          <FavouriteStar itemType="task" itemId={editingTask.id} label={editingTask.title} variant="light" />
        )}
        <button type="button" onClick={requestCloseForm} aria-label="Close"
          className="w-8 h-8 rounded-lg bg-white/15 hover:bg-white/25 text-white flex items-center justify-center transition-colors focus:outline-none casevo-focus">✕</button>
      </div>
      <div className="flex items-center gap-2 mb-3.5 flex-wrap pr-24">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide px-2.5 py-1 rounded-md bg-white/20">
          <IcTask className="w-3.5 h-3.5" />{editable ? (editingTask ? 'Edit task' : 'New task') : 'Task'}
        </span>
        <span className={`inline-flex items-center text-[11px] font-semibold uppercase tracking-wide px-2.5 py-1 rounded-md ${statusPillCls}`}>{statusLabel}</span>
        <span
          className={`inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide px-2.5 py-1 rounded-md ${priorityHeroCls}`}
          title={`${PRIORITY_LABELS[formPriority]} priority`}
        >
          <PriorityGlyph p={formPriority} className="w-3 h-3" />{PRIORITY_LABELS[formPriority]} priority
        </span>
      </div>
      {editable ? (
        <>
          <input
            value={formTitle}
            data-field="task.title"
            onChange={(e) => {
              const v = e.target.value
              setFormTitle(v)
              if (v.trim()) autosaveEditField({ title: v.trim() })
            }}
            placeholder="Task title…"
            disabled={!canEditFully}
            className="w-full bg-white/10 border border-white/25 rounded-lg px-3 py-2 text-lg font-bold text-white placeholder-white/55 focus:outline-none focus:border-white/50 disabled:opacity-70 disabled:cursor-not-allowed"
          />
          <FieldError message={titleError ?? undefined} />
        </>
      ) : (
        <>
          <div className={`text-[22px] font-bold leading-tight ${isDone ? 'line-through opacity-80' : ''}`}>{formTitle}</div>
          <div className="mt-4 text-sm">
            {isDone ? (
              <div className="flex items-center gap-2.5">
                <IcCheckCircle />
                <span>
                  <span className="block text-[10px] uppercase tracking-wide opacity-70">Completed</span>
                  <span className="font-semibold">{editingTask ? fmtDay(editingTask.updatedAt) : ''}</span>
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-2.5">
                <IcCalendar />
                <span>
                  <span className="block text-[10px] uppercase tracking-wide opacity-70">Due</span>
                  <span className={`font-semibold ${isOverdue ? 'text-[#ffb3ab]' : ''}`}>
                    {dueDisplay}{isOverdue ? ' · Overdue' : ''}
                  </span>
                </span>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )

  return (
    // Opened FROM the calendar → NO page chrome (transparent, no AppHeader, no
    // min-h-screen bg): App keeps CalendarView mounted BEHIND, and TasksView
    // draws ONLY its fixed modal overlay so the calendar shows dimmed through
    // the backdrop. Opened from the LIST → the normal full-page shell.
    <div className={openedFromCalendar ? '' : 'min-h-screen md:min-h-full bg-casevo-offwhite font-sans'}>
      {!openedFromCalendar && (
        <AppHeader
          onWordmarkClick={handleHome}
          leftHamburger={<SidebarHamburger />}
          leftSlot={<BackButton onClick={handleBack} forceShow={mode === 'form'} />}
        />
      )}
      <div className={openedFromCalendar ? '' : showsTaskList ? 'max-w-[1180px] mx-auto px-4 sm:px-[36px] pt-6 sm:pt-[30px] pb-[60px]' : 'max-w-3xl mx-auto px-4 py-6 sm:py-8'}>
        {showOpeningState ? (
          /* OPENING a specific task (deep-link) — clean Casevo loading state.
             From the calendar it's a fixed backdrop overlay (calendar dimmed
             behind); otherwise an in-page placeholder. */
          <div className={openedFromCalendar
            ? 'fixed inset-0 z-[70] bg-black/40 flex items-center justify-center text-casevo-mid'
            : 'flex items-center justify-center min-h-[60vh] text-casevo-mid'}>
            <div className="flex flex-col items-center gap-3">
              <div className="h-6 w-6 rounded-full border-2 border-casevo-mint border-t-casevo-brand animate-spin" />
              <span className="text-sm">Opening task…</span>
            </div>
          </div>
        ) : showsTaskList ? (
          /* ★ MY WORK — the prioritised execution queue (list mode). All data +
             actions are wired here; MyWork renders the mockup (hero, tiles,
             slice bar, urgency queue, calendar + team-load rails). */
          <MyWork
            tasks={tasks}
            loading={tasksLoading}
            scopeLoading={scopeLoading}
            error={tasksError}
            currentUserId={currentUserId}
            isAdmin={isAdmin}
            canSeeFirm={canSeeFirmTasks}
            canAssignPeople={canAssignTasksToOthers}
            canAssignFirm={canCreateFirmWideTasks}
            firmUsers={firmUsers}
            caseInfo={caseInfoFor}
            clientLabel={(id) => clientLabelFor(id)}
            scope={scope}
            onScope={(s) => { setScope(s); setUnclaimedOnly(false); setSetByMeOnly(false) }}
            overdueOnly={overdueOnly}
            onOverdueOnly={setOverdueOnly}
            initialDueWindow={initialDueSoon ? 'soon' : undefined}
            unclaimedOnly={unclaimedOnly}
            onUnclaimedOnly={(v) => { setUnclaimedOnly(v); if (v) setSetByMeOnly(false) }}
            setByMeOnly={setByMeOnly}
            onSetByMeOnly={(v) => { setSetByMeOnly(v); if (v) { setUnclaimedOnly(false); setScope('mine') } }}
            summary={summary}
            calendarEntries={todayEntries}
            highlightedTaskId={highlightedTaskId}
            onNewTask={handleNewTask}
            onOpenTask={handleEditTask}
            onOpenTaskById={(id) => setPendingTaskId(id)}
            onOpenCase={(id) => { void handleOpenLinkedCase(id) }}
            onOpenClient={handleOpenLinkedClient}
            onOpenCalendar={handleOpenCalendar}
            onUpdate={handleQuickUpdate}
            onDismiss={(t) => { void handleDismissRow(t) }}
            canDismiss={canDismissRow}
            canEditFully={canEditTaskFully}
            emptyState={(hasFilters, clear) => (
              <div className="flex flex-col items-center justify-center text-center px-4 py-16 sm:py-20 min-h-[40vh]">
                <TasksEmptyIcon />
                <h2 className="text-casevo-forest font-medium text-xl sm:text-2xl mb-3">
                  {hasFilters ? 'No tasks match your filters' : 'No tasks yet'}
                </h2>
                <p className="text-casevo-mid text-sm sm:text-base max-w-md leading-relaxed mb-4">
                  {hasFilters
                    ? 'Try clearing the filters to see your tasks.'
                    : 'Create your first task to track what needs doing.'}
                </p>
                {hasFilters ? (
                  <button type="button" onClick={clear} className="text-sm text-casevo-brand hover:text-casevo-forest underline">
                    Clear filters
                  </button>
                ) : (
                  <button type="button" onClick={handleNewTask}
                    className={BTN_SECONDARY_SM}>
                    <PlusIcon />
                    New task
                  </button>
                )}
              </div>
            )}
          />
        ) : (
          /* FORM MODE — hero-band card with a read-only VIEW / EDIT split.
             The card carries onBlur/onInput so the near-live relay + focus
             ownership fire for the band title (data-field="task.title") and
             every data-field input in the edit body below.

             The task ALWAYS renders as a CENTERED overlay (fixed inset-0 · flex
             items-center justify-center), whether opened from the calendar OR
             the tasks list. THEME: opened FROM THE CALENDAR → the outer wrapper
             carries the calendar's OWN focus theme (dark/casevo-light), which
             the darkMode variant selector (&:is(.dark *):not(.casevo-light *))
             honours OVER the app-level `.dark` on <html> — exactly the scope
             AppointmentFormModal renders inside. Opened from the LIST → no scope
             class, so it follows the normal app theme. */
          <div className={openedFromCalendar ? (calendarDark ? 'dark' : 'casevo-light') : ''}>
          <div className="fixed inset-0 z-[70] overflow-y-auto bg-black/40">
          <div className="flex min-h-full items-center justify-center p-4">
          <div
            className="rounded-2xl overflow-hidden bg-casevo-surface border border-casevo-border shadow-2xl w-full max-w-3xl"
            onBlur={handleTaskFieldBlur}
            onInput={handleTaskFieldInput}
          >
            {taskHeroBand(shownTaskView === 'edit')}
            {shownTaskView === 'view' && editingTask ? (
              /* ═══════════════ VIEW MODE (read-only) ═══════════════ */
              <>
                {formError && (
                  <ErrorNote className="mx-6 sm:mx-8 mt-4">{formError}</ErrorNote>
                )}
                <div className="px-6 sm:px-8 py-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                    {/* Details — assignee + linked case OR client (click-through) */}
                    <div>
                      <div className={bhCls}><IcFile />Details</div>
                      <div className="mb-4">
                        <div className="text-[11.5px] font-semibold text-casevo-mid mb-1.5">Lead</div>
                        {(() => {
                          const a = getFirmUserById(formAssigneeId)
                          return (
                            <div className="inline-flex items-center gap-2.5 border border-casevo-border dark:bg-[#1f2e26] rounded-lg px-2.5 py-2">
                              {a ? (
                                <UserAvatar userId={a.id} fallbackName={`${a.firstName} ${a.lastName}`.trim() || a.email} size="sm" title={false} />
                              ) : (
                                <span className="w-7 h-7 rounded-full bg-casevo-mint shrink-0" />
                              )}
                              <span className="text-sm font-medium text-casevo-forest">
                                {a ? `${a.firstName} ${a.lastName}${a.id === currentUserId ? ' (you)' : ''}` : 'Unassigned'}
                              </span>
                            </div>
                          )
                        })()}
                        {/* Assigned (to you) by … · Created by … — from the LIVE row. */}
                        <TaskProvenance task={(tasks ?? []).find((t) => t.id === editingTask.id) ?? editingTask}
                          firmUsers={firmUsers} currentUserId={currentUserId} className="mt-1.5" />
                      </div>
                      {/* Audience chips — read from the LIVE store row so a
                          remote change (entity-changed summary) shows here. */}
                      {(() => {
                        const live = (tasks ?? []).find((t) => t.id === editingTask.id) ?? editingTask
                        const ids = (live.assignees ?? []).map((a) => a.userId).filter((id) => id !== live.assignedToUserId)
                        if (!live.firmWide && ids.length === 0) return null
                        return (
                          <div className="mb-4">
                            <div className="text-[11.5px] font-semibold text-casevo-mid mb-1.5">{live.firmWide ? 'Assigned to' : 'Also assigned'}</div>
                            <AssigneeChips firmWide={live.firmWide} userIds={ids} firmUsers={firmUsers} currentUserId={currentUserId} />
                          </div>
                        )
                      })()}
                      {formLinkType === 'case' && formCaseId ? (
                        <div>
                          <div className="text-[11.5px] font-semibold text-casevo-mid mb-1.5">Linked case</div>
                          {/* ★ Locked when the viewer can't access the case — no dead-end click. */}
                          <CaseLink caseId={formCaseId} onOpen={(id) => { void handleOpenLinkedCase(id) }} lockedClassName="text-sm font-semibold"
                            className="inline-flex items-center gap-2 text-sm font-semibold text-casevo-brand hover:text-casevo-forest hover:underline focus:outline-none casevo-focus">
                            <IcFile className="w-4 h-4" />{caseLabelFor(formCaseId) || `Case · ${caseShortId(formCaseId)}`} →
                          </CaseLink>
                        </div>
                      ) : formLinkType === 'client' && formClientId ? (
                        <div>
                          <div className="text-[11.5px] font-semibold text-casevo-mid mb-1.5">Linked client</div>
                          <button type="button" onClick={() => handleOpenLinkedClient(formClientId)}
                            className="inline-flex items-center gap-2 text-sm font-semibold text-casevo-brand hover:text-casevo-forest hover:underline focus:outline-none casevo-focus">
                            <IcClientLink className="w-4 h-4" />{clientLabelFor(formClientId) || 'Open client'} →
                          </button>
                        </div>
                      ) : (
                        <div>
                          <div className="text-[11.5px] font-semibold text-casevo-mid mb-1.5">Linked to</div>
                          <div className="text-sm text-casevo-mid">Not linked</div>
                        </div>
                      )}
                    </div>
                    {/* Notes — rich HTML rendered into a soft panel */}
                    <div>
                      <div className={bhCls}><IcNote />Notes</div>
                      {viewDescription && viewDescription.replace(/<[^>]*>/g, '').trim() ? (
                        /* ★ FAST PATH: the stored copy (task.description) — instant,
                           no live-doc connection. Every note whose copy is in sync
                           (incl. ones the open-repair already fixed) lands here. */
                        <div
                          className={NOTE_PANEL_CLS}
                          // eslint-disable-next-line react/no-danger
                          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(viewDescription) }}
                        />
                      ) : editingTask ? (
                        /* ★ Mirror empty → read the LIVE collab doc (the same
                           source edit mode shows), read-only. The stored
                           task.description is only a debounced mirror of the
                           Yjs doc and can lag/miss it (edit showed the note,
                           view said "No notes."). If the doc has text and this
                           user may edit, the editor's mirror-repair writes it
                           back to task.description → formDescription fills →
                           this falls through to the plain render above. */
                        <RichTextEditor
                          key={`view:task:${editingTask.id}:description`}
                          collabDocName={`task:${editingTask.id}:description`}
                          collabToken={() => localStorage.getItem('casevo_token') ?? ''}
                          initialHtml=""
                          collabReadOnly
                          collabEmptyText="No notes."
                          collabReadOnlyClassName={NOTE_PANEL_CLS}
                          onSnapshot={canEditFully
                            ? (html) => { setFormDescription(html); autosaveEditField({ description: html }) }
                            : undefined}
                          collabUser={collabUser}
                        />
                      ) : (
                        <div className="text-sm text-casevo-mid">No notes.</div>
                      )}
                    </div>
                  </div>
                </div>
                {/* "Last edited by" — only when someone other than the owner
                    edited last; nothing until the column is live (batch 2). */}
                {editingTask && (
                  <LastEditedLine
                    className="px-6 sm:px-8 pb-3"
                    editorId={editingTask.lastEditedByUserId}
                    editedAt={editingTask.lastEditedAt}
                    ownerId={editingTask.assignedToUserId}
                  />
                )}
                {/* Footer — Delete · Mark done/Reopen · Edit */}
                <div className="flex items-center gap-2.5 px-6 sm:px-8 py-4 border-t border-casevo-border bg-casevo-input">
                  {canDeleteTask && (
                    <button type="button" onClick={() => setShowDeleteConfirm(true)} disabled={formSubmitting}
                      className="mr-auto px-4 py-2.5 rounded-lg border border-casevo-border text-sm font-semibold text-red-600 dark:text-red-300 hover:border-red-300 dark:hover:border-red-400/60 disabled:opacity-50 focus:outline-none casevo-focus">Delete</button>
                  )}
                  {/* ★ STATUS-USERS (can change status, can't fully edit — e.g. a
                      co-assignee on someone else's task, a firm-wide participant)
                      get the full Open / In progress / Done control here, since
                      the locked edit form (where "In progress" lived) is gone.
                      Full editors keep Mark done/Reopen + Edit (unchanged). */}
                  {canChangeStatus && !canEditFully && (
                    <div role="radiogroup" aria-label="Task status"
                      className={`${canDeleteTask ? '' : 'mr-auto'} inline-flex h-[38px] border border-casevo-border rounded-lg overflow-hidden bg-casevo-surface`}>
                      {([['open', 'To do'], ['in_progress', 'In progress'], ['done', 'Done']] as [TaskStatus, string][]).map(([value, label], i) => {
                        const on = formStatus === value
                        return (
                          <button key={value} type="button" role="radio" aria-checked={on}
                            disabled={formSubmitting}
                            onClick={() => { void handleSetStatus(value) }}
                            className={`px-3 text-xs font-semibold whitespace-nowrap transition-colors focus:outline-none casevo-focus disabled:opacity-50 ${i > 0 ? 'border-l border-casevo-border' : ''} ${
                              on ? 'bg-casevo-mint text-casevo-forest' : 'text-casevo-mid hover:text-casevo-forest hover:bg-casevo-input'
                            }`}>
                            {label}
                          </button>
                        )
                      })}
                    </div>
                  )}
                  {canChangeStatus && canEditFully && (<button type="button" onClick={() => { void handleToggleDone() }} disabled={formSubmitting}
                    className={`${canDeleteTask ? '' : 'mr-auto'} inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg border text-sm font-semibold focus:outline-none casevo-focus disabled:opacity-50 ${isDone ? 'border-casevo-border text-casevo-forest hover:border-casevo-mid' : 'border-casevo-brand text-casevo-brand hover:bg-casevo-mint/40'}`}>
                    <IcCheckCircle className="w-4 h-4" />{isDone ? 'Reopen' : 'Mark done'}
                  </button>)}
                  {/* ★ Edit only for users who can FULLY edit (admin / owner /
                      creator-on-task / editColleaguesTasks — canEditFully, the
                      same rule that unlocks the fields). It used to show for
                      everyone, dropping non-editors into an all-disabled "edit"
                      form. Mark done/Reopen (canChangeStatus) stays separate. */}
                  {canEditFully ? (
                    <button type="button" onClick={() => setTaskView('edit')}
                      className={BTN_PRIMARY_MD}>Edit</button>
                  ) : (
                    <p className={`${canDeleteTask || canChangeStatus ? 'ml-auto' : 'mr-auto'} text-xs text-casevo-mid`}>
                      {canChangeStatus ? 'You can update the status' : 'View only'}{editingOwner ? ` — ${editingOwner.firstName} ${editingOwner.lastName}’s task` : ''}.
                    </p>
                  )}
                </div>
              </>
            ) : (
              /* ═══════════════ EDIT / CREATE MODE ═══════════════ */
              <>
                {!canEditFully && (
                  <div className="mx-6 sm:mx-8 mt-4 px-4 py-3 bg-casevo-mint/30 border border-casevo-border rounded-lg text-sm text-casevo-forest">
                    {editingOwner ? `This is ${editingOwner.firstName} ${editingOwner.lastName}’s task` : 'This task belongs to someone else'} &mdash; {canChangeStatus ? 'you can update the status, but other fields are read-only.' : 'view only.'}
                  </div>
                )}
                <div className="px-6 sm:px-8 py-6 space-y-4">
                  {/* Notes (rich text). The title now lives in the hero band. */}
                  <FormField label="Notes" fieldKey="task.description">
                    {editingTask && canEditFully ? (
                      /* Editing an EXISTING task → Yjs collaborative rich-text (Stage
                         4). Doc `task:<id>:description` (WHITELIST column-token
                         `description`), keyed so switching tasks remounts. HTML
                         snapshot mirrors to the `description` column via the per-task
                         autosave; near-live sync of this field is removed (Yjs owns
                         it), and a 409 on `description` resolves silently
                         (TASK_COLLAB_COLUMNS). Seeding/read-only/caret inherited. */
                      <RichTextEditor
                        key={`task:${editingTask.id}:description`}
                        collabDocName={`task:${editingTask.id}:description`}
                        collabToken={() => localStorage.getItem('casevo_token') ?? ''}
                        initialHtml={DOMPurify.sanitize(formDescription || '')}
                        onSnapshot={(html) => {
                          setFormDescription(html)
                          autosaveEditField({ description: html })
                        }}
                        placeholder="Optional notes about this task"
                        collabUser={collabUser}
                      />
                    ) : editingTask ? (
                      /* Read-only (assigned viewer): render the saved HTML, not raw
                         tags. */
                      <div
                        className="w-full border border-casevo-border rounded px-3 py-2 text-sm bg-casevo-input text-casevo-forest min-h-[4.5rem] [&_p]:m-0"
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(formDescription || '') }}
                      />
                    ) : (
                      /* CREATE mode: no task id yet → plain textarea, commit-on-submit
                         (no collab doc to bind to until the task exists). */
                      <textarea
                        value={formDescription}
                        onChange={(e) => setFormDescription(e.target.value)}
                        placeholder="Optional notes about this task"
                        rows={3}
                        className="w-full border border-casevo-border rounded px-3 py-2 text-sm bg-casevo-input text-casevo-forest focus:outline-none focus:border-casevo-mid resize-y"
                      />
                    )}
                  </FormField>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FormField label="Due date" fieldKey="task.due-date">
                      <div className={!canEditFully ? 'pointer-events-none opacity-60' : ''}>
                        {/* allowFuture: task due dates are inherently in the
                            future — without it the shared DateInput's past-only
                            year cap silently returns null (same bug fixed for
                            deadline due dates). */}
                        {/* Date + time in a wrapping row (DateTimeRow): side by
                            side when the column has room, time under the date
                            when it doesn't — never overlapping. */}
                        <DateTimeRow
                          className="!gap-y-2"
                          date={
                            <div className="min-w-0">
                              <DateInput value={formDueDate} onChange={(v) => { commitDue(v, formDueTime) }} allowFuture ariaLabel="Due date" />
                              {/* Amber, never blocks: in the past / more than 5 years ahead. */}
                              {taskDueWarning(formDueDate, londonToday()) && <span className="block text-[12px] text-casevo-warn mt-1" role="status">{taskDueWarning(formDueDate, londonToday())}</span>}
                            </div>
                          }
                          time={
                            /* Diary: optional due TIME. Empty = whole-day. Clearing
                               it reverts the task to all-day; setting it places the
                               task on that hour slot in the calendar's day/week view. */
                            <input
                              type="time"
                              value={formDueTime ?? ''}
                              onChange={(e) => { commitDue(formDueDate, e.target.value || null) }}
                              disabled={!formDueDate}
                              aria-label="Due time (optional)"
                              className="w-full border border-casevo-border rounded px-3 py-2 text-sm bg-casevo-input text-casevo-forest focus:outline-none focus:border-casevo-mid disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-gray-50 dark:disabled:bg-casevo-border"
                            />
                          }
                        />
                      </div>
                    </FormField>
                    {/* Status → segmented control (To do / In progress / Done).
                        data-field keeps it in the near-live focus map; the click
                        handler flushes + broadcasts the status like the old select. */}
                    <FormField label="Status" fieldKey="task.status">
                      <div className="inline-flex rounded-lg border border-casevo-border overflow-hidden" data-field="task.status">
                        {(['open', 'in_progress', 'done'] as TaskStatus[]).map((s) => (
                          <button
                            key={s}
                            type="button"
                            disabled={!canChangeStatus}
                            onClick={() => {
                              setFormStatus(s)
                              autosaveEditField({ status: s })
                              void (async () => {
                                await flushTaskAutoSave()
                                if (editingTask) sendTaskFieldNow(editingTask.id, 'task.status')
                              })()
                            }}
                            className={`px-3 py-2 text-xs font-semibold focus:outline-none casevo-focus transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${formStatus === s ? 'bg-casevo-primary text-white' : 'bg-casevo-input text-casevo-mid hover:text-casevo-forest'}`}
                          >
                            {s === 'open' ? 'To do' : s === 'in_progress' ? 'In progress' : 'Done'}
                          </button>
                        ))}
                      </div>
                    </FormField>
                  </div>
                  {/* Priority → segmented control (Low / Normal / High), same
                      pattern as Status: set + autosave + flush + live broadcast
                      (task.priority is in TASK_KEY_TO_FIELD). Selected High uses
                      the severity red; Low/Normal use the brand fill. Gated by
                      canEditFully like every non-status field. */}
                  <FormField label="Priority" fieldKey="task.priority">
                    <div className="inline-flex rounded-lg border border-casevo-border overflow-hidden" data-field="task.priority">
                      {(['low', 'normal', 'high'] as TaskPriority[]).map((p) => (
                        <button
                          key={p}
                          type="button"
                          disabled={!canEditFully}
                          aria-pressed={formPriority === p}
                          onClick={() => {
                            setFormPriority(p)
                            autosaveEditField({ priority: p })
                            void (async () => {
                              await flushTaskAutoSave()
                              if (editingTask) sendTaskFieldNow(editingTask.id, 'task.priority')
                            })()
                          }}
                          className={`inline-flex items-center gap-1 px-3 py-2 text-xs font-semibold focus:outline-none casevo-focus transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
                            formPriority === p
                              ? (p === 'high' ? 'bg-[#c23b2c] text-white dark:bg-[#b8483a]' : 'bg-casevo-primary text-white')
                              : 'bg-casevo-input text-casevo-mid hover:text-casevo-forest'
                          }`}
                        >
                          <PriorityGlyph p={p} />{PRIORITY_LABELS[p]}
                        </button>
                      ))}
                    </div>
                  </FormField>
                  {/* ★ Assign to — one list: the first person is the LEAD
                      (assignedToUserId: due-today + edit alerts, team load,
                      Group by Owner), the rest co-assignees. "Make lead" swaps
                      (admin / current lead / creator). Read-only for a worker
                      on someone else's task (canEditFully). */}
                  <FormField label="Assign to" fieldKey="task.assigned-to">
                    <TaskAssigneesField
                      value={formAssign}
                      onChange={commitAssign}
                      canPeople={canAssignTasksToOthers}
                      canFirm={canCreateFirmWideTasks}
                      canChangeLead={canChangeLead}
                      disabled={!canEditFully}
                      firmUsers={firmUsers}
                      currentUserId={currentUserId}
                      lossWarning={assignLossWarning}
                    />
                    {editingTask && (
                      <TaskProvenance task={(tasks ?? []).find((t) => t.id === editingTask.id) ?? editingTask}
                        firmUsers={firmUsers} currentUserId={currentUserId} className="mt-2" />
                    )}
                  </FormField>
                  <FormField label="Link to">
                    <select
                      value={formLinkType}
                      onChange={(e) => {
                        const next = e.target.value as 'none' | 'case' | 'client'
                        setFormLinkType(next)
                        if (next !== 'case') setFormCaseId('')
                        if (next !== 'client') setFormClientId('')
                        // Switching link type clears the non-matching id(s). Persist
                        // the cleared link immediately (a picked id autosaves below).
                        if (next === 'none') autosaveEditField({ caseId: null, clientId: null })
                        else if (next === 'case') autosaveEditField({ clientId: null })
                        else autosaveEditField({ caseId: null })
                      }}
                      disabled={!canEditFully}
                      className="w-full border border-casevo-border rounded px-3 py-2 text-sm bg-casevo-input text-casevo-forest focus:outline-none focus:border-casevo-mid disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-gray-50 dark:disabled:bg-casevo-border"
                    >
                      <option value="none">Not linked</option>
                      <option value="case">A case</option>
                      <option value="client">A client</option>
                    </select>
                    {formLinkType === 'case' && (
                      <select
                        value={formCaseId}
                        onChange={(e) => {
                          const v = e.target.value
                          setFormCaseId(v)
                          autosaveEditField({ caseId: v || null })
                        }}
                        disabled={!canEditFully}
                        className="w-full mt-2 border border-casevo-border rounded px-3 py-2 text-sm bg-casevo-input text-casevo-forest focus:outline-none focus:border-casevo-mid disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-gray-50 dark:disabled:bg-casevo-border"
                      >
                        <option value="">Choose a case</option>
                        {allCases.map((c) => (
                          <option key={c.id} value={c.id}>
                            {caseNameWithRef(c)}
                          </option>
                        ))}
                      </select>
                    )}
                    {/* Diary batch: jump straight to the linked case (only for a
                        saved task with a chosen case). Back from the case returns
                        to Tasks (caseOpenedFrom='taskList'). */}
                    {formLinkType === 'case' && editingTask && formCaseId && (
                      <div className="mt-2">
                        <CaseLink caseId={formCaseId} onOpen={(id) => { void handleOpenLinkedCase(id) }} lockedClassName="text-xs"
                          className="text-xs text-casevo-brand hover:text-casevo-forest hover:underline focus:outline-none focus-visible:underline">
                          Open case →
                        </CaseLink>
                      </div>
                    )}
                    {formLinkType === 'client' && (
                      <div className="mt-2">
                        <button
                          type="button"
                          onClick={() => setShowClientPicker(true)}
                          disabled={!canEditFully}
                          className="w-full text-left border border-casevo-border rounded px-3 py-2 text-sm bg-casevo-input text-casevo-forest hover:border-casevo-mid focus:outline-none focus:border-casevo-mid disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-gray-50 dark:disabled:bg-casevo-border"
                        >
                          {formClientId
                            ? clientLabelFor(formClientId) || 'Choose a client'
                            : 'Choose a client'}
                        </button>
                        {/* Client click-through (saved task with a chosen client) */}
                        {editingTask && formClientId && (
                          <button
                            type="button"
                            onClick={() => handleOpenLinkedClient(formClientId)}
                            className="mt-2 text-xs text-casevo-brand hover:text-casevo-forest hover:underline focus:outline-none focus-visible:underline"
                          >
                            Open client →
                          </button>
                        )}
                      </div>
                    )}
                  </FormField>
                  {formError && (
                    <ErrorNote>{formError}</ErrorNote>
                  )}
                </div>
                {/* Footer — Cancel (existing → back to VIEW; new → close) · Save */}
                <div className="flex items-center justify-end gap-2.5 px-6 sm:px-8 py-4 border-t border-casevo-border bg-casevo-input">
                  <button
                    type="button"
                    onClick={() => {
                      // Existing task → return to its read-only VIEW; a brand-new
                      // task → close to wherever the form was opened FROM.
                      if (editingTask) setTaskView('view')
                      else requestCloseForm()
                    }}
                    disabled={formSubmitting}
                    className="px-4 py-2.5 rounded-lg border border-casevo-border text-sm font-semibold text-casevo-mid hover:text-casevo-forest hover:border-casevo-mid disabled:opacity-50 focus:outline-none casevo-focus"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => { void handleSubmit() }}
                    disabled={formSubmitting}
                    className={BTN_PRIMARY_MD}
                  >
                    {formSubmitting ? 'Saving…' : editingTask ? 'Save changes' : 'Create task'}
                  </button>
                </div>
              </>
            )}
            <ClientPickerModal
              isOpen={showClientPicker}
              onClose={() => setShowClientPicker(false)}
              onClientSelected={(clientId) => {
                setFormClientId(clientId)
                setShowClientPicker(false)
                autosaveEditField({ clientId })
              }}
              mode="pick"
            />
            <ConfirmDialogue
              isOpen={showDeleteConfirm}
              title="Delete task?"
              message="This task will be permanently removed."
              confirmLabel="Delete"
              onConfirm={() => { void handleDelete() }}
              onCancel={() => setShowDeleteConfirm(false)}
            />
          </div>
          </div>
          </div>
          </div>
        )}
      </div>
    </div>
  )
}
export default TasksView
